<template>
  <div class="page-outer">
    <div class="page-header page-header-line-msg-editor">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯 LINE 訊息範本' : '新增 LINE 訊息範本' }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="goBack">取消</button>
        <button class="btn btn-primary" @click="handleSubmit" :disabled="saving">
          <span v-if="saving">儲存中...</span>
          <span v-else>{{ isEdit ? '更新' : '建立' }}</span>
        </button>
      </div>
    </div>

  <div class="page-container">
    <div v-if="loading" class="loading-state"><div class="spinner"></div><p>載入中...</p></div>

    <div v-else class="editor-layout">
      <!-- 手機版預覽開關 -->
      <div class="preview-toggle-bar">
        <button class="preview-toggle-btn" @click="mobilePreviewOpen = !mobilePreviewOpen">
          <i :class="mobilePreviewOpen ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
          {{ mobilePreviewOpen ? '關閉預覽' : '開啟即時預覽' }}
        </button>
      </div>

      <!-- 手機版預覽抽屜 -->
      <div v-if="mobilePreviewOpen" class="mobile-preview-drawer">
        <div class="mobile-preview-inner">
          <div class="mobile-preview-header">
            <span><i class="ri-smartphone-line"></i> 即時預覽</span>
            <button class="mobile-preview-close" @click="mobilePreviewOpen = false">
              <i class="ri-close-line"></i>
            </button>
          </div>
          <MessagePreview
            :messageType="form.messageType"
            :textContent="previewText"
            :textEmojis="previewTextEmojis"
            :imageUrl="previewImageUrl"
            :previewImageUrl="previewVideoThumb"
            :imagemapUrl="previewImagemapUrl"
            :imagemapAreas="previewImagemapAreas"
            :carouselItems="previewCarouselItems"
            :flexContent="previewFlexContent"
          />
        </div>
      </div>

      <!-- 左側：編輯區 -->
      <div class="editor-panel">
        <!-- 基本資訊 -->
        <div class="card">
          <div class="card-body">
            <h2 class="section-title">基本資訊</h2>
            <div class="form-group">
              <label class="form-label required-label">範本名稱</label>
              <input v-model="form.name" type="text" class="form-control" placeholder="例如：商品促銷卡片" maxlength="100" />
            </div>
            <div class="form-group">
              <label class="form-label">說明</label>
              <input v-model="form.description" type="text" class="form-control" placeholder="範本用途描述" maxlength="500" />
            </div>
            <div class="form-group">
              <label class="form-label required-label">範本用途</label>
              <div class="audience-options">
                <label class="audience-option" :class="{ active: form.audienceType === 'personal' }">
                  <input v-model="form.audienceType" type="radio" value="personal" class="sr-only" />
                  <span class="audience-radio-dot"></span>
                  <span class="audience-content">
                    <span class="audience-title"><i class="ri-user-line"></i> 個人訊息</span>
                    <small>一對一回覆／自動回覆／Postback 使用；可插入並替換 <code v-pre>{{變數}}</code></small>
                  </span>
                </label>
                <label class="audience-option" :class="{ active: form.audienceType === 'broadcast' }">
                  <input v-model="form.audienceType" type="radio" value="broadcast" class="sr-only" />
                  <span class="audience-radio-dot"></span>
                  <span class="audience-content">
                    <span class="audience-title"><i class="ri-broadcast-line"></i> 群發訊息</span>
                    <small>LINE 推播等多人發送；內容固定，不進行會員變數替換</small>
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- 訊息類型選擇 -->
        <div class="card">
          <div class="card-body">
            <h2 class="section-title">訊息類型</h2>
            <div class="type-grid">
              <div
                v-for="t in msgTypes"
                :key="t.value"
                class="type-card"
                :class="{ selected: form.messageType === t.value }"
                @click="setMessageType(t.value)"
              >
                <i :class="t.icon"></i>
                <span>{{ t.label }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 訊息內容編輯 -->
        <div v-if="form.messageType" class="card">
          <div class="card-body">
            <h2 class="section-title">訊息內容</h2>

            <!-- AltText（圖文訊息在 ImagemapEditor 內；其餘類型顯示於此） -->
            <div v-if="needsAltText && form.messageType !== 'imagemap'" class="form-group" style="margin-bottom:16px;">
              <label class="form-label required-label">替代文字（AltText）</label>
              <input v-model="form.altText" type="text" class="form-control" placeholder="不支援 Flex 時顯示的文字" maxlength="400" />
            </div>

            <TextEditor
              v-if="form.messageType === 'text'"
              ref="textEditorRef"
              v-model="msgData"
              :substitute-variables="isPersonalAudience"
            />
            <ImageEditor v-else-if="form.messageType === 'image'" v-model="msgData" />
            <VideoEditor v-else-if="form.messageType === 'video'" v-model="msgData" />
            <ImageCarouselEditor v-else-if="form.messageType === 'image_carousel'" v-model="msgData" />
            <ImagemapEditor
              v-else-if="form.messageType === 'imagemap'"
              v-model="msgData"
              :alt-text="form.altText"
              @update:alt-text="form.altText = $event"
            />
            <FlexEditor v-else-if="form.messageType === 'flex'" v-model="msgData" />
            <FlexCarouselEditor v-else-if="form.messageType === 'flex_carousel'" v-model="msgData" />
          </div>
        </div>

        <!-- 變數宣告（僅個人訊息） -->
        <div v-if="isPersonalAudience && ['text', 'image_carousel', 'flex', 'flex_carousel'].includes(form.messageType)" class="card">
          <div class="card-body">
            <h2 class="section-title">變數宣告</h2>
            <TemplateVariableShortcuts
              v-model="form.variables"
              :items="availableVars"
              behavior="toggle"
              :title="''"
              @insert="onLineVariableShortcutInsert"
            >
              <template #hint>
                <p class="form-hint">
                  點擊可<strong>加入或取消</strong>變數宣告；<strong>加入時</strong>若為<strong>文字訊息</strong>會一併插入到<strong>訊息內容</strong>游標處（請先點一下訊息框再選）。圖片輪播、Flex 等請在對應欄位自行輸入標籤（如 <code v-pre>{{lineName}}</code>），發送時自動替換。
                  用於「點數過期設定」提醒時，系統會帶入 <code v-pre>{{memberName}}</code>、<code v-pre>{{totalPoints}}</code>、<code v-pre>{{expiryDate}}</code> 等；與禮物發送的 <code v-pre>{{pointsAmount}}</code> 不同。
                </p>
              </template>
            </TemplateVariableShortcuts>
          </div>
        </div>
      </div>

      <!-- 右側：即時預覽 -->
      <div class="preview-panel">
        <div class="preview-sticky preview-sticky-editor">
          <h3 class="preview-title"><i class="ri-smartphone-line"></i> 即時預覽</h3>
          <MessagePreview
            :messageType="form.messageType"
            :textContent="previewText"
            :textEmojis="previewTextEmojis"
            :imageUrl="previewImageUrl"
            :previewImageUrl="previewVideoThumb"
            :imagemapUrl="previewImagemapUrl"
            :imagemapAreas="previewImagemapAreas"
            :carouselItems="previewCarouselItems"
            :flexContent="previewFlexContent"
          />
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';
import TextEditor from './components/TextEditor.vue';
import ImageEditor from './components/ImageEditor.vue';
import VideoEditor from './components/VideoEditor.vue';
import ImageCarouselEditor from './components/ImageCarouselEditor.vue';
import ImagemapEditor from './components/ImagemapEditor.vue';
import FlexEditor from './components/FlexEditor.vue';
import FlexCarouselEditor from './components/FlexCarouselEditor.vue';
import MessagePreview from './components/MessagePreview.vue';
import TemplateVariableShortcuts from '@/components/TemplateVariableShortcuts.vue';
import { editorRowsToLineApi, lineApiToEditor } from '@/utils/lineEmojiPlaceholder.js';
import { buildActionUri, actionValueFromUri } from '@/utils/lineActionValue.js';

const router = useRouter();
const route = useRoute();

const isEdit = computed(() => !!route.params.id);
const loading = ref(false);
const saving = ref(false);
const mobilePreviewOpen = ref(false);

const form = reactive({
  name: '',
  description: '',
  messageType: '',
  altText: '',
  variables: [],
  status: 'active',
  audienceType: 'broadcast'
});

const isPersonalAudience = computed(() => form.audienceType === 'personal');

const msgData = ref({});
const textEditorRef = ref(null);

const msgTypes = [
  { value: 'text', label: '文字', icon: 'ri-text' },
  { value: 'image', label: '圖片', icon: 'ri-image-line' },
  { value: 'video', label: '影片', icon: 'ri-video-line' },
  { value: 'image_carousel', label: '圖片輪播', icon: 'ri-gallery-line' },
  { value: 'imagemap', label: '圖文', icon: 'ri-collage-line' },
  { value: 'flex', label: 'Flex', icon: 'ri-layout-3-line' },
  { value: 'flex_carousel', label: 'Flex輪播', icon: 'ri-layout-grid-line' }
];

/** 與個人通知／自動發禮／點數到期提醒等發送時注入的變數鍵一致（見 notificationSender.replaceVariables） */
const availableVars = [
  { key: 'lineName', label: 'LINE 顯示名稱' },
  { key: 'memberName', label: '會員姓名' },
  { key: 'memberCode', label: '會員編號' },
  { key: 'pointsAmount', label: '點數數量（註冊禮／升等禮等自動發送）' },
  { key: 'couponCount', label: '優惠券數量' },
  { key: 'tierName', label: '會員等級' },
  { key: 'totalPoints', label: '即將到期點數合計（點數到期提醒）' },
  { key: 'expiryDate', label: '最近到期日 YYYY-MM-DD（點數到期提醒）' },
  { key: 'daysUntilExpiry', label: '距離到期曆天數（點數到期提醒）' },
  { key: 'reminderDays', label: '本輪提醒閾值／到期前天數（點數到期提醒）' }
];

const allowedVariableKeys = new Set(availableVars.map((v) => v.key));

function sanitizeDeclaredVariables(keys) {
  return (keys || []).filter((k) => allowedVariableKeys.has(k));
}

const needsAltText = computed(() =>
  ['flex', 'flex_carousel', 'imagemap', 'image_carousel'].includes(form.messageType)
);

/** @returns {string|null} 錯誤訊息，無誤則 null */
function validateLineEmojisJson(md) {
  const raw = (md.emojisJson || '').trim();
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return 'LINE emoji：JSON 格式不正確';
  }
  if (!Array.isArray(parsed)) return 'LINE emoji：必須為 JSON 陣列';
  const seenSlot = new Set();
  for (let i = 0; i < parsed.length; i++) {
    const e = parsed[i];
    const slot = Number(e?.slot);
    if (!Number.isFinite(slot) || slot < 1 || !Number.isInteger(slot)) {
      return `LINE emoji 第 ${i + 1} 筆：slot 須為正整數（與訊息中 {{$1}}、{{$2}} 編號一致）`;
    }
    if (seenSlot.has(slot)) return `LINE emoji：slot ${slot} 重複`;
    seenSlot.add(slot);
    if (typeof e?.productId !== 'string' || !e.productId.trim()) {
      return `LINE emoji 第 ${i + 1} 筆：productId 須為字串`;
    }
    if (e?.emojiId === undefined || e?.emojiId === null || String(e.emojiId).trim() === '') {
      return `LINE emoji 第 ${i + 1} 筆：emojiId 必填`;
    }
  }
  return null;
}

function buildTextMessageContent(md) {
  const raw = (md.emojisJson || '').trim();
  let rows = [];
  if (raw) {
    rows = JSON.parse(raw);
  }
  if (!Array.isArray(rows)) rows = [];
  const r = editorRowsToLineApi(md.text || '', rows);
  if (r.error) {
    throw new Error(r.error);
  }
  const textMsg = { type: 'text', text: r.text };
  if (r.emojis?.length) textMsg.emojis = r.emojis;
  return textMsg;
}

/** 佔位 {{$n}} 與 JSON slot 是否一致（儲存前） */
function validateTextEmojiBrackets(md) {
  let rows = [];
  const raw = (md.emojisJson || '').trim();
  if (raw) {
    try {
      rows = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(rows)) return null;
  const r = editorRowsToLineApi(md.text || '', rows);
  return r.error || null;
}

// Preview：換算為 LINE 的 text + emojis（與實際送出一致），供 LineMessagePreview 還原圖示
const previewLineResolved = computed(() => {
  if (form.messageType !== 'text') return { text: '', emojis: [] };
  const raw = (msgData.value.emojisJson || '').trim();
  let rows = [];
  if (raw) {
    try {
      rows = JSON.parse(raw);
    } catch {
      return { text: msgData.value.text || '', emojis: [] };
    }
  }
  if (!Array.isArray(rows) || rows.length === 0) {
    return { text: msgData.value.text || '', emojis: [] };
  }
  const r = editorRowsToLineApi(msgData.value.text || '', rows);
  if (r.error || !r.emojis?.length) {
    return { text: msgData.value.text || '', emojis: [] };
  }
  return { text: r.text, emojis: r.emojis };
});

const previewText = computed(() => previewLineResolved.value.text);
const previewTextEmojis = computed(() => previewLineResolved.value.emojis);
const previewImageUrl = computed(() => msgData.value.originalContentUrl || '');
const previewVideoThumb = computed(() => msgData.value.previewImageUrl || '');
const previewImagemapUrl = computed(() => msgData.value.baseUrl || '');
const previewCarouselItems = computed(() => msgData.value.columns || []);

const IMAGEMAP_LAYOUTS = {
  full: [{ x: 0, y: 0, w: 1, h: 1 }],
  h2: [{ x: 0, y: 0, w: 1, h: .5 }, { x: 0, y: .5, w: 1, h: .5 }],
  v2: [{ x: 0, y: 0, w: .5, h: 1 }, { x: .5, y: 0, w: .5, h: 1 }],
  h3: [{ x: 0, y: 0, w: 1, h: 1/3 }, { x: 0, y: 1/3, w: 1, h: 1/3 }, { x: 0, y: 2/3, w: 1, h: 1/3 }],
  quad: [{ x: 0, y: 0, w: .5, h: .5 }, { x: .5, y: 0, w: .5, h: .5 }, { x: 0, y: .5, w: .5, h: .5 }, { x: .5, y: .5, w: .5, h: .5 }],
  t1b2: [{ x: 0, y: 0, w: 1, h: .5 }, { x: 0, y: .5, w: .5, h: .5 }, { x: .5, y: .5, w: .5, h: .5 }],
  t2b1: [{ x: 0, y: 0, w: .5, h: .5 }, { x: .5, y: 0, w: .5, h: .5 }, { x: 0, y: .5, w: 1, h: .5 }],
  six: [
    { x: 0, y: 0, w: 1/3, h: .5 }, { x: 1/3, y: 0, w: 1/3, h: .5 }, { x: 2/3, y: 0, w: 1/3, h: .5 },
    { x: 0, y: .5, w: 1/3, h: .5 }, { x: 1/3, y: .5, w: 1/3, h: .5 }, { x: 2/3, y: .5, w: 1/3, h: .5 }
  ]
};
const previewImagemapAreas = computed(() =>
  msgData.value.layout ? (IMAGEMAP_LAYOUTS[msgData.value.layout] || []) : []
);

const previewFlexContent = computed(() => {
  if (form.messageType === 'flex' || form.messageType === 'flex_carousel') {
    if (msgData.value.messageContent) return msgData.value.messageContent;
    if (msgData.value.templateFields) {
      return compiledPreview.value;
    }
  }
  return null;
});

const compiledPreview = ref(null);
let compileTimeout = null;

watch(() => form.audienceType, (v) => {
  if (v === 'broadcast') {
    form.variables = [];
  }
});

watch(() => msgData.value.templateFields, () => {
  const fp = msgData.value.flexPreset;
  if (!fp || fp === 'custom') return;
  clearTimeout(compileTimeout);
  compileTimeout = setTimeout(async () => {
    try {
      if (fp === 'carousel_mixed' && Array.isArray(msgData.value.templateFields)) {
        // Flex Carousel: compile each bubble independently
        const bubbles = msgData.value.templateFields;
        const compiled = await Promise.all(bubbles.map(async (b) => {
          if (!b.preset || b.preset === 'custom') {
            return b.json ? (typeof b.json === 'string' ? JSON.parse(b.json) : b.json) : null;
          }
          const res = await lineMsgTemplateService.compileFlex(b.preset, b.fields || {});
          return (res?.success && res.data) ? res.data : null;
        }));
        compiledPreview.value = { type: 'carousel', contents: compiled.filter(Boolean) };
      } else {
        // Single Flex
        const res = await lineMsgTemplateService.compileFlex(fp, msgData.value.templateFields);
        if (res?.success && res.data) {
          compiledPreview.value = res.data;
        }
      }
    } catch (e) { /* ignore */ }
  }, 400);
}, { deep: true });

function setMessageType(type) {
  form.messageType = type;
  msgData.value = {};
  compiledPreview.value = null;
}

function onLineVariableShortcutInsert(key) {
  const tag = `{{${key}}}`;
  if (form.messageType === 'text' && textEditorRef.value?.insertPlainTextAtCaret) {
    textEditorRef.value.insertPlainTextAtCaret(tag);
  }
}

function buildPayload() {
  const payload = {
    name: form.name,
    description: form.description,
    messageType: form.messageType,
    altText: form.altText || undefined,
    variables: form.audienceType === 'broadcast' ? [] : sanitizeDeclaredVariables(form.variables),
    status: form.status,
    audienceType: form.audienceType === 'broadcast' ? 'broadcast' : 'personal'
  };

  if (form.messageType === 'text') {
    payload.messageContent = buildTextMessageContent(msgData.value);
  } else if (form.messageType === 'image') {
    payload.messageContent = {
      type: 'image',
      originalContentUrl: msgData.value.originalContentUrl || '',
      previewImageUrl: msgData.value.previewImageUrl || msgData.value.originalContentUrl || ''
    };
  } else if (form.messageType === 'video') {
    payload.messageContent = {
      type: 'video',
      originalContentUrl: msgData.value.originalContentUrl || '',
      previewImageUrl: msgData.value.previewImageUrl || ''
    };
  } else if (form.messageType === 'image_carousel') {
    const cols = (msgData.value.columns || []).filter(c => c.imageUrl);
    payload.messageContent = {
      type: 'template', altText: form.altText || '圖片',
      template: {
        type: 'image_carousel',
        columns: cols.map(c => {
          const actionType = c.actionType || 'uri';
          let action;
          if (actionType === 'message') {
            action = { type: 'message', label: c.label || '查看', text: c.text || '' };
          } else if (actionType === 'postback') {
            action = { type: 'postback', label: c.label || '查看', data: c.data || '' };
            if (c.displayText) action.displayText = c.displayText;
          } else {
            // 網址、分享好友、電話最後都是 uri（分享／電話的 uri 由欄位組出來）
            action = { type: 'uri', label: c.label || '查看', uri: buildActionUri(c) || 'https://example.com' };
          }
          return { imageUrl: c.imageUrl, action };
        })
      }
    };
  } else if (form.messageType === 'imagemap') {
    let actions = [];
    try { actions = JSON.parse(msgData.value.actionsJson || '[]'); } catch (e) { /* */ }
    payload.messageContent = {
      type: 'imagemap', baseUrl: msgData.value.baseUrl || '', altText: form.altText || '圖文',
      baseSize: { width: 1040, height: msgData.value.baseHeight || 1040 },
      actions
    };
    payload.templateFields = {
      layout: msgData.value.layout || null,
      areaLinks: msgData.value.areaLinks || null
    };
  } else if (form.messageType === 'flex' || form.messageType === 'flex_carousel') {
    payload.flexPreset = msgData.value.flexPreset || 'custom';
    payload.templateFields = msgData.value.templateFields || null;
    payload.messageContent = msgData.value.messageContent || compiledPreview.value || {};
  }

  return payload;
}

async function handleSubmit() {
  if (!form.name) { alert('請填寫範本名稱'); return; }
  if (!form.messageType) { alert('請選擇訊息類型'); return; }
  if (form.messageType === 'text') {
    const emojiErr = validateLineEmojisJson(msgData.value);
    if (emojiErr) {
      alert(emojiErr);
      return;
    }
    const bracketErr = validateTextEmojiBrackets(msgData.value);
    if (bracketErr) {
      alert(bracketErr);
      return;
    }
  }
  if (form.messageType === 'video') {
    if (!msgData.value.originalContentUrl) {
      alert('請上傳影片檔案');
      return;
    }
    if (!String(msgData.value.previewImageUrl || '').trim()) {
      alert('請上傳影片縮圖（必填）');
      return;
    }
  }

  saving.value = true;
  try {
    const payload = buildPayload();
    if (isEdit.value) {
      await lineMsgTemplateService.update(route.params.id, payload);
    } else {
      await lineMsgTemplateService.create(payload);
    }
    router.push({ name: 'LineMsgTemplates' });
  } catch (err) {
    console.error('儲存失敗:', err);
    alert('儲存失敗：' + (err?.response?.data?.message || err.message || '未知錯誤'));
  } finally {
    saving.value = false;
  }
}

async function loadTemplate() {
  loading.value = true;
  try {
    const res = await lineMsgTemplateService.getById(route.params.id);
    if (res?.success && res.data) {
      const d = res.data;
      form.name = d.name || '';
      form.description = d.description || '';
      form.messageType = d.messageType || '';
      form.altText = d.altText || '';
      form.variables = sanitizeDeclaredVariables(d.variables);
      form.status = d.status || 'active';
      form.audienceType = d.audienceType === 'personal' ? 'personal' : 'broadcast';

      const md = {};
      if (d.messageType === 'text') {
        const conv = lineApiToEditor(d.messageContent?.text || '', d.messageContent?.emojis);
        md.text = conv.text;
        md.emojisJson = conv.rows.length ? JSON.stringify(conv.rows, null, 2) : '';
      } else if (d.messageType === 'image') {
        md.originalContentUrl = d.messageContent?.originalContentUrl || '';
        md.previewImageUrl = d.messageContent?.previewImageUrl || '';
      } else if (d.messageType === 'video') {
        md.originalContentUrl = d.messageContent?.originalContentUrl || '';
        md.previewImageUrl = d.messageContent?.previewImageUrl || '';
      } else if (d.messageType === 'image_carousel') {
        md.columns = (d.messageContent?.template?.columns || []).map(c => {
          const lineType = c.action?.type || 'uri';
          // LINE 只存得下 uri，載入時從網址反推回「分享好友／電話」讓使用者接著編輯
          const inferred = lineType === 'uri' ? actionValueFromUri(c.action?.uri || '') : {};
          return {
            imageUrl: c.imageUrl || '',
            label: c.action?.label || '',
            actionType: inferred.actionType || lineType,
            uri: c.action?.uri || '',
            text: c.action?.text || '',
            data: c.action?.data || '',
            displayText: c.action?.displayText || '',
            phone: inferred.phone || '',
            shareText: inferred.shareText || ''
          };
        });
      } else if (d.messageType === 'imagemap') {
        md.baseUrl = d.messageContent?.baseUrl || '';
        md.baseHeight = d.messageContent?.baseSize?.height || 1040;
        md.actionsJson = d.messageContent?.actions ? JSON.stringify(d.messageContent.actions, null, 2) : '';
        md.layout = d.templateFields?.layout || null;
        md.areaLinks = d.templateFields?.areaLinks || null;
      } else if (d.messageType === 'flex' || d.messageType === 'flex_carousel') {
        md.flexPreset = d.flexPreset || 'custom';
        md.templateFields = d.templateFields || {};
        md.messageContent = d.messageContent || null;
      }
      msgData.value = md;
    }
  } catch (err) {
    console.error('載入範本失敗:', err);
    alert('載入範本失敗');
    goBack();
  } finally {
    loading.value = false;
  }
}

function goBack() {
  router.push({ name: 'LineMsgTemplates' });
}

onMounted(() => {
  if (isEdit.value) loadTemplate();
});
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-right: 0;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.btn {
  padding: 6px 16px;
  border-radius: 6px;
}
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover:not(:disabled) { background: #e5e7eb; }

.page-container {
  padding-top: 72px;
}

.editor-layout {
  display: block;
}

.editor-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-right: 348px;
}

.preview-panel {
  /* DOM container only – actual preview is position: fixed */
}

@media (max-width: 1024px) {
  .editor-panel {
    margin-right: 0;
  }
  .preview-panel {
    display: none;
  }
  .preview-sticky {
    display: none;
  }
}

/* 手機版預覽開關（桌機隱藏） */
.preview-toggle-bar {
  display: none;
}

@media (max-width: 1024px) {
  .preview-toggle-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }

  .preview-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: 1px solid #06C755;
    border-radius: 20px;
    background: #fff;
    color: #06C755;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all .15s;
  }

  .preview-toggle-btn:hover {
    background: #f0fff5;
  }

  .mobile-preview-drawer {
    margin-bottom: 16px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    background: #fff;
  }

  .mobile-preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }

  .mobile-preview-header i {
    color: #06C755;
    margin-right: 6px;
  }

  .mobile-preview-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 6px;
    background: #f3f4f6;
    color: #6b7280;
    cursor: pointer;
    font-size: 16px;
  }

  .mobile-preview-close:hover {
    background: #e5e7eb;
  }

  .mobile-preview-inner {
    padding: 16px;
  }
}

.card {
  background: #fff; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,.08);
}
.card-body { padding: 20px; }

.section-title {
  font-size: 15px; font-weight: 600; color: #374151;
  margin: 0 0 16px; padding-bottom: 12px; border-bottom: 1px solid #f3f4f6;
}

.form-group { margin-bottom: 14px; }
.form-label { display: block; font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 6px; }
.required-label::after { content: ' *'; color: #ef4444; }
.form-control {
  width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px;
  font-size: 14px; box-sizing: border-box;
}
.form-control:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.1); }
.form-hint { font-size: 13px; color: #6b7280; margin: -4px 0 8px; line-height: 1.5; }
.form-hint code { font-size: 11px; padding: 1px 4px; background: #f3f4f6; border-radius: 3px; }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.audience-options {
  display: flex;
  flex-direction: row;
  gap: 12px;
}

.audience-option {
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  background: #fafafa;
}

.audience-option:hover {
  border-color: #93c5fd;
  background: #f5f9ff;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.08);
}

.audience-option.active {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
}

.audience-radio-dot {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  margin-top: 2px;
  transition: border-color 0.15s;
  position: relative;
}

.audience-option.active .audience-radio-dot {
  border-color: #3b82f6;
}

.audience-option.active .audience-radio-dot::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background: #3b82f6;
  border-radius: 50%;
}

.audience-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.audience-title {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.audience-option small {
  display: block;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

@media (max-width: 640px) {
  .audience-options {
    flex-direction: column;
  }
}

.audience-option small code {
  font-size: 11px;
  padding: 1px 4px;
  background: #f3f4f6;
  border-radius: 3px;
}

.type-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)); gap: 10px;
}
.type-card {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 14px 8px; border: 2px solid #e5e7eb; border-radius: 10px;
  cursor: pointer; transition: all .2s; text-align: center;
}
.type-card i { font-size: 24px; color: #9ca3af; }
.type-card span { font-size: 12px; color: #6b7280; font-weight: 500; }
.type-card.selected { border-color: #3b82f6; background: #eff6ff; }
.type-card.selected i { color: #3b82f6; }
.type-card.selected span { color: #3b82f6; }
.type-card:hover { border-color: #93c5fd; }

.preview-panel {
  position: relative;
  align-self: start;
}
.preview-sticky {
  position: fixed;
  top: 120px;
  right: 28px;
  width: 320px;
  max-height: calc(100vh - 140px);
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 90;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  padding: 12px 0 0;
}
.preview-title {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 600; color: #374151;
  padding: 0 16px 12px;
  border-bottom: 1px solid #e5e7eb;
}
.preview-title i { font-size: 18px; color: #06C755; }

.loading-state {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  padding: 60px; color: #6b7280;
}
.spinner {
  width: 32px; height: 32px; border: 3px solid #e5e7eb;
  border-top-color: #3b82f6; border-radius: 50%;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>

<style>
.page-header-line-msg-editor {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}
.sidebar.collapsed ~ .main-content .page-header-line-msg-editor {
  left: 64px;
}
@media (max-width: 768px) {
  .page-header-line-msg-editor {
    left: 0 !important;
    top: 56px;
  }
}
</style>
